import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {IScrollDataInput} from '../../models';

/**
 * ### SharedScrollingInfiniteComponent
 * A reusable infinite scroll component that triggers an event when the user scrolls close to the bottom.
 *
 * @description
 * This component displays a scrollable container with an item template to render dynamic data.
 * It supports lazy loading by emitting `scrolledToThreshold` when the scroll position is within a configurable pixel threshold
 * from the bottom, allowing the parent to load more data.
 *
 * It also supports projected content for no-data and loading states via content selectors.
 *
 * @example
 * <app-shared-scrolling-infinite
 *   [scrollData]="myScrollData"
 *   [scrollThresholdPx]="150"
 *   [itemTemplate]="myTemplate"
 *   (scrolledToThreshold)="loadMoreItems()"
 * >
 *   <ng-template noData>
 *     <p>No items to display</p>
 *   </ng-template>
 *
 *   <ng-template scrollLoader>
 *     <mat-progress-spinner mode="indeterminate"></mat-progress-spinner>
 *   </ng-template>
 * </app-shared-scrolling-infinite>
 *
 * @contentChild noData - Content displayed when there is no data.
 * @contentChild scrollLoader - Content displayed while data is loading.
 */
@Component({
    selector: 'app-shared-scrolling-infinite',
    templateUrl: './shared-scrolling-infinite.component.html',
    styleUrls: ['./shared-scrolling-infinite.component.scss'],
    standalone: false
})
export class SharedScrollingInfiniteComponent implements AfterViewInit, OnChanges {

  /**
   * Property name to uniquely identify items in the list.
   * @type {string}
   * @default 'id'
   * @example 'userId'
   */
  @Input() idProperty = 'id';

  /**
   * Indicates whether the scroll data is currently loading.
   * When true, the scroll loading template is shown.
   * @type {boolean}
   * @default false
   */
  @Input() isScrollDataLoading = false;

  /**
   * Template reference to render each item in the scroll list.
   * @type {TemplateRef<unknown>}
   */
  @Input() itemTemplate!: TemplateRef<unknown>;

  /**
   * Input object containing the data array to render.
   * @type {IScrollDataInput<unknown>}
   */
  @Input() scrollData!: IScrollDataInput<unknown>;

  /**
   * Number of pixels from the bottom of the scroll container at which
   * the `scrolledToThreshold` event will be emitted.
   * @type {number}
   * @default 200
   * @example 150
   */
  @Input() scrollThresholdPx = 200;

  /**
   * When false, does not emit `scrolledToThreshold` just because the list is shorter than the panel.
   * Use for filtered lists (e.g. failed/unread notifications) where auto-fill would page entire history.
   */
  @Input() enableAutoFillWhenShort = true;

  /**
   * Increment when scroll list content changes without replacing the `scrollData` input reference.
   */
  @Input() scrollDataRevision = 0;

  /**
   * Event emitted when the user scrolls within `scrollThresholdPx` pixels from the bottom
   * and `isScrollDataLoading` is false.
   * Used to trigger loading additional data.
   * @event
   */
  @Output() readonly scrolledToThreshold = new EventEmitter<void>();

  /**
   * Reference to the scroll container element.
   * @type {ElementRef<HTMLDivElement>}
   */
  @ViewChild('scrollContainer') scrollContainer!: ElementRef<HTMLDivElement>;

  private lastAutoFillCount = -1;

  /**
   * Returns the array of data to render, or empty array if none.
   * @readonly
   * @type {unknown[]}
   */
  get data(): unknown[] {
    return Array.isArray(this.scrollData?.data) ? this.scrollData.data as unknown[] : [];
  }

  ngAfterViewInit(): void {
    this.loadMoreIfListDoesNotOverflow();
  }

  ngOnChanges(changes: SimpleChanges): void {
    let shouldScheduleAutoFill = false;

    if (changes['scrollDataRevision'] && this.enableAutoFillWhenShort) {
      if (!changes['scrollDataRevision'].firstChange) {
        this.lastAutoFillCount = -1;
      }
      shouldScheduleAutoFill = true;
    }
    if (changes['isScrollDataLoading']) {
      shouldScheduleAutoFill = true;
    }
    if (changes['scrollData']) {
      const previousData: unknown[] | undefined = changes['scrollData'].previousValue?.data as unknown[] | undefined;
      const currentData: unknown[] = this.data;
      const previousLength: number = Array.isArray(previousData) ? previousData.length : -1;
      const dataReferenceChanged: boolean = previousData !== currentData;
      const dataLengthChanged: boolean = previousLength !== currentData.length;
      if (changes['scrollData'].firstChange || dataReferenceChanged || dataLengthChanged) {
        shouldScheduleAutoFill = true;
      }
    }
    if (changes['enableAutoFillWhenShort'] && changes['enableAutoFillWhenShort'].currentValue === true) {
      shouldScheduleAutoFill = true;
    }
    if (shouldScheduleAutoFill) {
      this.scheduleAutoFillCheck();
    }
  }

  private scheduleAutoFillCheck(): void {
    queueMicrotask((): void => {
      this.loadMoreIfListDoesNotOverflow();
    });
  }

  /**
   * Requests another page when the list is shorter than the panel, so paging
   * does not depend on a scroll that cannot happen.
   */
  private loadMoreIfListDoesNotOverflow(): void {
    if (!this.enableAutoFillWhenShort) {
      return;
    }
    const element: HTMLDivElement | undefined = this.scrollContainer?.nativeElement;
    if (!element || this.isScrollDataLoading || this.data.length === 0) {
      return;
    }
    if (element.scrollHeight > element.clientHeight + 1) {
      this.lastAutoFillCount = this.data.length;
      return;
    }
    if (this.data.length === this.lastAutoFillCount) {
      return;
    }
    this.lastAutoFillCount = this.data.length;
    this.scrolledToThreshold.emit();
  }

  /**
   * Scroll event handler that checks if the user has scrolled within the threshold
   * from the bottom of the scroll container and emits the load event if appropriate.
   *
   * @param {Event} event - The scroll event.
   * @returns {void}
   */
  onScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    const distanceFromBottom: number = scrollHeight - scrollTop - clientHeight;
    if (distanceFromBottom < this.scrollThresholdPx && !this.isScrollDataLoading) {
      this.scrolledToThreshold.emit();
    }
  }

  /**
   * TrackBy function for ngFor, tracking items by their unique identifier property.
   *
   * @param {number} index - The index of the item.
   * @param {unknown} item - The item object.
   * @returns {string} The unique identifier string for the item.
   */
  trackById(index: number, item: unknown): string {
    return String(item?.[this.idProperty]) ?? index.toString();
  }

}
